import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  Phone,
  Plus,
  Edit2,
  X,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function SupervisorHub() {
  const [loading, setLoading] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  const [dailyData, setDailyData] = useState({
    attendance: [],
    logs: [],
  });
  
  // 📅 DATE STATE
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "" });

  const navigate = useNavigate();

  useEffect(() => {
    fetchAllData();
  }, [selectedDate]);

  async function fetchAllData() {
    try {
      setLoading(true);
      
      // 1. Fetch all supervisors
      const { data: sups, error: supErr } = await supabase
        .from("supervisors")
        .select("*")
        .order("name", { ascending: true });

      // 2. Fetch attendance for selected date
      const { data: att, error: attErr } = await supabase
        .from("attendance")
        .select("supervisor_id")
        .eq("date", selectedDate);

      // 3. Fetch financial logs for selected date
      const { data: logs, error: logErr } = await supabase
        .from("supervisor_logs")
        .select("supervisor_id, food_expenses, advance_taken, room_expenses, travel_expenses, other_expenses")
        .eq("date", selectedDate);

      if (supErr || attErr || logErr) throw new Error("Data sync failed");

      setSupervisors(sups || []);
      setDailyData({
        attendance: att?.map(a => a.supervisor_id) || [],
        logs: logs || []
      });

    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  // --- DATE NAVIGATION ---
  const handlePrevDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // --- CALCULATIONS ---
  const totalMoneyGivenToday = dailyData.logs.reduce((sum, log) => 
    sum + Number(log.food_expenses || 0) + Number(log.advance_taken || 0) + 
    Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0), 0
  );

  const getSupervisorMoney = (id) => {
    return dailyData.logs
      .filter(l => l.supervisor_id === id)
      .reduce((sum, log) => sum + Number(log.food_expenses || 0) + Number(log.advance_taken || 0) + 
      Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0), 0);
  };

  // --- CRUD ACTIONS ---
  const handleOpenModal = (sup = null) => {
    if (sup) {
      setEditingId(sup.id);
      setFormData({ name: sup.name, phone: sup.phone || "" });
    } else {
      setEditingId(null);
      setFormData({ name: "", phone: "" });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await supabase.from("supervisors").update(formData).eq("id", editingId);
        toast.success("Updated");
      } else {
        await supabase.from("supervisors").insert([formData]);
        toast.success("Added");
      }
      setIsModalOpen(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans">
      <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">
        
        {/* 🔷 HEADER & DATE SELECTOR */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-black text-[#0054A6]">Team Dashboard</h1>
            <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
              <div className="h-2 w-2 bg-[#0054A6] rounded-full animate-pulse" />
              <span className="text-[#0054A6] font-black text-xs uppercase tracking-tighter">Live Monitoring</span>
            </div>
          </div>

          <div className="flex items-center gap-4 bg-slate-100 p-2 rounded-[2rem]">
            <button onClick={handlePrevDate} className="p-2 hover:bg-white rounded-full transition-all shadow-sm">
              <ChevronLeft size={20} className="text-[#0054A6]" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-2xl shadow-sm border border-slate-200">
              <CalendarIcon size={16} className="text-[#0054A6]" />
              <span className="font-black text-sm text-slate-700">
                {new Date(selectedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <button onClick={handleNextDate} className="p-2 hover:bg-white rounded-full transition-all shadow-sm">
              <ChevronRightIcon size={20} className="text-[#0054A6]" />
            </button>
          </div>
        </div>

        {/* 🔷 STATS BAR */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Present" count={dailyData.attendance.length} subtext="Active today" color="text-emerald-600" />
          <StatCard label="Absent" count={supervisors.length - dailyData.attendance.length} subtext="No attendance" color="text-rose-500" />
          <StatCard label="Cash Flow" count={`₹${totalMoneyGivenToday.toLocaleString()}`} subtext="Total Given Today" />
          <StatCard label="Team Size" count={supervisors.length} subtext="Registered Staff" />
        </div>

        {/* 🔷 MAIN CONTENT */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h3 className="text-lg font-black text-slate-800">Personnel Status</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Calculated for {selectedDate}</p>
            </div>
            <button onClick={() => handleOpenModal()} className="bg-[#0054A6] text-white px-6 py-3 rounded-2xl font-black text-sm flex items-center gap-2 shadow-lg transition-all active:scale-95">
              <Plus size={18} strokeWidth={3} /> Add Supervisor
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center py-20 gap-4">
               <div className="h-8 w-8 border-4 border-slate-100 border-t-[#0054A6] rounded-full animate-spin" />
               <p className="text-slate-400 font-bold text-xs uppercase">Syncing Date Data...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {supervisors.map((sup) => {
                const isPresent = dailyData.attendance.includes(sup.id);
                const moneyGiven = getSupervisorMoney(sup.id);

                return (
                  <div
                    key={sup.id}
                    onClick={() => navigate(`/supervisor/${sup.id}`)}
                    className="group flex items-center justify-between bg-slate-50/50 p-5 rounded-3xl border border-transparent hover:border-blue-100 hover:bg-white hover:shadow-xl transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-5">
                      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center font-black text-white text-xl ${isPresent ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                        {sup.name?.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-black text-slate-900 group-hover:text-[#0054A6] transition-colors">{sup.name}</p>
                          <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase ${isPresent ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-slate-400'}`}>
                            {isPresent ? 'Present' : 'Absent'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1.5 text-xs text-slate-400 font-bold tracking-tight">
                            <Phone size={12} className="text-[#0054A6]" /> {sup.phone || "No Phone"}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-[#0054A6] font-black bg-blue-50 px-2 py-0.5 rounded-lg">
                             Given: ₹{moneyGiven.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button onClick={(e) => { e.stopPropagation(); handleOpenModal(sup); }} className="p-3 text-slate-300 hover:text-[#0054A6] transition-all">
                        <Edit2 size={20} />
                      </button>
                      <ChevronRight className="text-slate-300 group-hover:text-[#0054A6] transition-all" size={24} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 🔷 MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95">
            <div className="bg-[#0054A6] p-8 text-white flex justify-between items-center">
              <h2 className="text-xl font-black">{editingId ? "Edit Info" : "Register Staff"}</h2>
              <button onClick={() => setIsModalOpen(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <input required className="w-full p-4 bg-slate-100 rounded-2xl outline-none font-bold text-slate-700" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              <input className="w-full p-4 bg-slate-100 rounded-2xl outline-none font-bold text-slate-700" placeholder="Phone Number" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              <button type="submit" className="w-full bg-[#0054A6] text-white p-5 rounded-[2rem] font-black uppercase tracking-widest text-xs">Confirm</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, count, subtext, color = "text-[#0054A6]" }) {
  return (
    <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100 text-center group transition-all">
      <p className="text-[10px] font-black uppercase text-slate-300 tracking-widest mb-1">{label}</p>
      <h2 className={`text-3xl font-black ${color}`}>{count}</h2>
      <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-tighter">{subtext}</p>
    </div>
  );
}